import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MainLayout } from "../../layout/mainLayout";
import { useAppDispatch, useAppSelector } from "../../../framework/customStore";
import { MuiDataGrid } from "../../framework/muiDataGrid";
import {
    GRID_DETAIL_PANEL_TOGGLE_FIELD,
    GridColDef,
    GridRenderCellParams,
    GridRowId,
} from "@mui/x-data-grid-premium";
import { t } from "i18next";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import AssignmentIcon from "@mui/icons-material/Assignment";

import {
    fetchAllVehicleGroups,
    fetchVehicleOptions,
} from "../../../store/vehicleSlice";
import { VehicleListItemDto } from "../../../models/transport/vehicle";
import { fetchGroupedCostCenterOptions } from "../../../store/costCenterSlice";
import { FuelType } from "../../../models/vehicle/fuelType";
import { useFetch } from "../../../hooks/useFetch";
import * as formAnswerService from "../../../services/formAnswerService";
import { VehicleFormAnswersPanel } from "./vehicleFormAnswersPanel";
import moment from "moment";
import { useAppOptionValueEquals } from "../../../hooks/useAppOption";
import { OptionFormsEnabled } from "../../../models/common/consts";

export const VehiclesView = () => {
    return (
        <MainLayout>
            <VehiclesViewContent />
        </MainLayout>
    );
};

export interface VehicleDetailsListItem extends VehicleListItemDto {
    vehicleGroupName: string;
    costCenterName: string;
    fuelTypeName: string;
}

export const VehiclesViewContent = () => {
    const { vehicles, allVehicleGroups } = useAppSelector(
        (state) => state.vehicle
    );
    const costCenters = useAppSelector(
        (state) => state.costCenter.groupedOptions
    );
    const dispatch = useAppDispatch();

    const [formsEnabled] = useAppOptionValueEquals(OptionFormsEnabled, true);

    const [vehicleIdsWithAnswers, setVehicleIdsWithAnswers] = useState<
        Set<string>
    >(new Set());

    const [expandedRowIds, setExpandedRowIds] = useState<GridRowId[]>([]);

    useFetch(
        (signal) =>
            formsEnabled
                ? formAnswerService.getRelatedObjectIdsWithAnswers("Vehicle", signal)
                : Promise.resolve({ data: [] }),
        {
            onSuccess: (response) =>
                setVehicleIdsWithAnswers(new Set(response.data)),
            withLoadingIndicator: false,
        },
        [formsEnabled]
    );

    const toggleDetailPanel = useCallback((rowId: string) => {
        setExpandedRowIds((prev) =>
            prev.includes(rowId) ? prev.filter((id) => id !== rowId) : [rowId]
        );
    }, []);

    const gridDef: GridColDef<VehicleDetailsListItem>[] = useMemo(
        () => [
            {
                headerName: t("common.vehicleNumber"),
                hideable: false,
                field: "registerNumber",
                flex: 1,
                type: "string",
            },
            {
                headerName: t("vehicle.shortNumber"),
                hideable: true,
                field: "number",
                type: "int",
                flex: 1,
            },
            {
                headerName: t("vehicle.abbreviation"),
                hideable: true,
                field: "abbreviation",
                flex: 1,
                type: "string",
            },
            {
                headerName: t("common.vehicleName"),
                hideable: true,
                field: "brand",
                type: "string",
                flex: 2,
            },
            {
                headerName: t("vehicle.vehicleGroup"),
                hideable: true,
                field: "vehicleGroupName",
                type: "string",
                flex: 2,
            },
            {
                headerName: t("vehicle.costCenter"),
                hideable: true,
                field: "costCenterName",
                type: "string",
                flex: 2,
            },
            {
                headerName: t("common.fuelTypes.fuelTypeTitle"),
                hideable: true,
                field: "fuelTypeName",
                type: "string",
                flex: 2,
            },
            {
                headerName: t("vehicle.inspectionDate"),
                hideable: true,
                field: "inspectionDate",
                type: "string",
                flex: 2,
                valueFormatter: (params) => {
                    return params?.value
                        ? moment(params?.value as string).format("DD/MM/YYYY")
                        : "";
                },
            },
            ...(formsEnabled
                ? [
                    {
                        field: "forms",
                        headerName: "",
                        width: 60,
                        sortable: false,
                        filterable: false,
                        disableColumnMenu: true,
                        renderCell: (
                            params: GridRenderCellParams<VehicleDetailsListItem>
                        ) =>
                            vehicleIdsWithAnswers.has(params.row.id) ? (
                                <Tooltip title={t("reporting.formSummary.tabTitle")}>
                                    <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={() => toggleDetailPanel(params.row.id)}
                                    >
                                        <AssignmentIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            ) : null,
                    },
                ]
                : []),
        ],
        [vehicleIdsWithAnswers, toggleDetailPanel, formsEnabled]
    );

    const getDetailPanelContent = useCallback(
        (params: { row: VehicleDetailsListItem }) =>
            formsEnabled ? (
                <VehicleFormAnswersPanel vehicleId={params.row.id} />
            ) : null,
        [formsEnabled]
    );

    const getDetailPanelHeight = useCallback(() => "auto" as const, []);

    const getVehicleGroupName = (vehicleGroupId?: string) => {
        return (
            allVehicleGroups?.find((vg) => vg.id === vehicleGroupId)?.name ?? ""
        );
    };

    const getCostCenterName = (costCenterId?: string) => {
        return (
            costCenters?.find((vg) => vg.value === costCenterId)?.label ?? ""
        );
    };

    // Load related data to state if not loaded yet
    useEffect(() => {
        // Load vehicles when view is opened to get fresh data to the list
        dispatch(fetchVehicleOptions() as any);

        if (!allVehicleGroups || allVehicleGroups.length === 0) {
            dispatch(fetchAllVehicleGroups([]) as any);
        }
        if (!costCenters || costCenters.length === 0) {
            dispatch(fetchGroupedCostCenterOptions() as any);
        }
    }, []);

    const visibleVehicles = useMemo(() => {
        return (vehicles ?? ([] as VehicleListItemDto[])).map((v) => {
            return {
                id: v.id,
                registerNumber: v.registerNumber,
                brand: v.brand,
                vehicleGroupId: v.vehicleGroupId,
                costCenterId: v.costCenterId,
                fuelType: v.fuelType,
                number: v.number,
                comment: v.comment,
                inspectionDate: v.inspectionDate,
                abbreviation: v.abbreviation,
                fuelTypeName: t(
                    `common.fuelTypes.fuelType.${FuelType[v.fuelType]}`
                ),
                vehicleGroupName: getVehicleGroupName(v.vehicleGroupId),
                costCenterName: getCostCenterName(v.costCenterId),
            };
        }) as VehicleDetailsListItem[];
    }, [vehicles]);

    return (
        <MuiDataGrid
            title={t("fleetManagementView.vehicles.listTitle")}
            columns={gridDef}
            rows={visibleVehicles}
            hideFooter
            getDetailPanelContent={getDetailPanelContent}
            getDetailPanelHeight={getDetailPanelHeight}
            detailPanelExpandedRowIds={expandedRowIds}
            onDetailPanelExpandedRowIdsChange={setExpandedRowIds}
            columnVisibilityModel={{
                [GRID_DETAIL_PANEL_TOGGLE_FIELD]: false,
            }}
        />
    );
};
